/**
 * I formati che l'utente puo' scegliere davvero: UNA dichiarazione sola.
 *
 * Prima l'elenco viveva in tre posti che non si parlavano: l'attributo
 * `accept` dei due input file, il filtro del drag and drop e lo `switch` di
 * `getExtractedText`. Divergevano in silenzio: c'erano formati che sapevamo
 * trascrivere e che nessuno poteva scegliere, e formati scegliibili che
 * cadevano su `default` e uscivano come "Nessun testo estratto", cioe' come
 * un file rotto.
 *
 * Qui l'elenco e' uno. Ogni estensione porta con se' la sua DESTINAZIONE: chi
 * la legge, e dove sta il codice che lo fa. Un test tiene insieme le due
 * estremita': niente estensione senza destinazione, niente destinazione senza
 * estensione.
 */

// Dove va a finire un file, cioe' chi ne ricava il testo.
export const PERCORSO = {
  // Passa dallo `switch` di getExtractedText (chiave: il mime del file).
  ESTRATTORE: 'estrattore',
  // processFile intercetta `file.type.startsWith('image/')` prima dello switch.
  IMMAGINE: 'immagine',
  // processFile legge il file come testo riconoscendolo dall'estensione:
  // .md e .markdown arrivano con `type` vuoto su quasi tutti i browser.
  TESTO: 'testo',
  // Il testo non lo ricaviamo noi: lo incolla l'utente.
  //
  // `.pages` e `.key` sono formati Apple che nessuna libreria del browser apre
  // e che il server non legge. Non per questo sono da togliere dalla scelta:
  // `useUploadFiles.js:54` e `:154` li intercettano PRIMA dell'estrazione e
  // chiamano `setShowTextArea(true)`, cioe' aprono la casella "incolla il
  // testo". Il ripiego e' deliberato e funziona su entrambi i percorsi, file
  // picker e drop. Toglierli dall'`accept` non correggerebbe niente: li
  // renderebbe grigi nel dialogo di sistema, e l'utente col `.pages` si
  // ritroverebbe senza il ripiego e senza una spiegazione.
  RIPIEGO_TESTUALE: 'ripiego-testuale',
};

/**
 * Ogni voce: l'estensione che l'utente vede nella finestra di scelta, i mime
 * che i browser attribuiscono a quell'estensione, e la destinazione che la
 * legge.
 *
 * I mime sono piu' di uno per estensione apposta: lo stesso `.mp3` arriva
 * `audio/mpeg` su Chrome e `audio/mp3` altrove, il `.m4a` puo' arrivare
 * `audio/x-m4a`, `audio/m4a` o `audio/aac`. Se manca la variante, il file
 * cade su `default`.
 *
 * Il ripiego testuale invece riconosce l'ESTENSIONE, non il mime: i formati
 * Apple arrivano con mime diversi a seconda del sistema, e `useUploadFiles`
 * guarda `file.name.endsWith(...)`. Per quelli il mime resta vuoto: dichiarare
 * mime che nessuno confronta sarebbe una bugia in piu' da mantenere.
 */
export const FORMATI_ACCETTATI = [
  { estensione: '.pdf', mime: ['application/pdf'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.txt', mime: ['text/plain'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.md', mime: ['text/markdown'], percorso: PERCORSO.TESTO },
  { estensione: '.markdown', mime: ['text/markdown'], percorso: PERCORSO.TESTO },
  { estensione: '.doc', mime: ['application/msword'], percorso: PERCORSO.ESTRATTORE },
  {
    estensione: '.docx',
    mime: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    percorso: PERCORSO.ESTRATTORE,
  },
  { estensione: '.jpg', mime: ['image/jpeg'], percorso: PERCORSO.IMMAGINE },
  { estensione: '.jpeg', mime: ['image/jpeg'], percorso: PERCORSO.IMMAGINE },
  { estensione: '.png', mime: ['image/png'], percorso: PERCORSO.IMMAGINE },
  { estensione: '.ppt', mime: ['application/vnd.ms-powerpoint'], percorso: PERCORSO.ESTRATTORE },
  {
    estensione: '.pptx',
    mime: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    percorso: PERCORSO.ESTRATTORE,
  },
  { estensione: '.pages', mime: [], percorso: PERCORSO.RIPIEGO_TESTUALE },
  { estensione: '.key', mime: [], percorso: PERCORSO.RIPIEGO_TESTUALE },
  { estensione: '.mp3', mime: ['audio/mpeg', 'audio/mp3', 'video/mp3'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.mp4', mime: ['video/mp4', 'audio/mp4'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.m4a', mime: ['audio/x-m4a', 'audio/m4a', 'audio/aac'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.mov', mime: ['video/quicktime'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.wav', mime: ['audio/wav', 'video/wav'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.webm', mime: ['audio/webm', 'video/webm'], percorso: PERCORSO.ESTRATTORE },
  { estensione: '.ogg', mime: ['audio/ogg', 'video/ogg'], percorso: PERCORSO.ESTRATTORE },
];

/** Il valore dell'attributo `accept` dell'input file. */
export const ACCEPT_ATTRIBUTE = FORMATI_ACCETTATI.map((f) => f.estensione).join(', ');

/** I mime che devono avere un `case` nello `switch` di getExtractedText. */
export const MIME_DELLO_SWITCH = FORMATI_ACCETTATI
  .filter((f) => f.percorso === PERCORSO.ESTRATTORE)
  .flatMap((f) => f.mime);

/** Le estensioni che `useUploadFiles` deve dirottare sulla casella del testo. */
export const ESTENSIONI_CON_RIPIEGO_TESTUALE = FORMATI_ACCETTATI
  .filter((f) => f.percorso === PERCORSO.RIPIEGO_TESTUALE)
  .map((f) => f.estensione);

/**
 * Toglie i parametri dal mime: `audio/webm;codecs=opus` -> `audio/webm`.
 * Lo `switch` confronta stringhe intere e il server ricava l'estensione dal
 * mime, quindi il tipo va ripulito prima di entrare in entrambi.
 */
export const mimePulito = (tipo) => String(tipo || '').split(';')[0].trim().toLowerCase();

/** L'estensione da mostrare all'utente quando un file non e' leggibile. */
export const estensioneDi = (nomeFile) => {
  const trovata = /\.([a-z0-9]+)$/i.exec(String(nomeFile || ''));
  return trovata ? `.${trovata[1].toLowerCase()}` : '';
};

// ---------------------------------------------------------------------------
// QUANTO GRANDE PUO' ESSERE UN FILE
//
// Prima c'era un solo controllo in tutta l'app: `> 25` MB, e SOLO per audio e
// video (uploadNotesForm.js). Ogni altro tipo non aveva nessun tetto
// dichiarato, quindi l'utente scopriva il muro soltanto quando il caricamento
// falliva, di solito senza un motivo leggibile.
//
// I 25 MB venivano dal fatto che l'audio saliva in base64 dentro un JSON e
// Cloud Run accetta ~32MB di corpo (il base64 gonfia di 4/3, tetto vero ~24MB).
// Ora quel vincolo non c'e' piu': sopra i 20MiB il file passa da Storage e il
// corpo della richiesta contiene solo il nome del blob.
//
// I numeri qui sotto non sono piu' il limite del TRASPORTO, sono il limite di
// cio' che il server riesce a masticare in tempo utile:
//
//   audio e video -> il vincolo vero e' la DURATA, non i megabyte. Il server
//     normalizza a mp3 mono 16kHz (un'ora sta in ~14MB, qualunque cosa arrivi)
//     e poi trascrive i segmenti da cinque minuti UNO DOPO L'ALTRO, perche'
//     vanno ricuciti in ordine. Con il timeout di Cloud Run a 1800s, tre ore di
//     lezione sono il punto in cui si comincia a rischiare. Non potendo
//     misurare la durata prima di leggere il file, si mette un tetto in
//     megabyte largo, che serve solo a fermare l'assurdo.
//
//   documenti -> Gemini accetta PDF fino a 50MB e 1000 pagine per chiamata, e
//     sopra quella soglia il server divide in blocchi da dieci pagine. Il costo
//     cresce con le pagine, non con i megabyte: un PDF scansionato pesa
//     ~333KB a pagina, quindi 300MB sono circa 900 pagine, che e' l'ordine di
//     grandezza giusto per un manuale intero.
//
// Se un giorno questi numeri vanno alzati, il posto e' questo e uno solo.
export const TETTO_AUDIO_VIDEO_MB = 500;
export const TETTO_DOCUMENTO_MB = 300;

/** Il tetto che vale per questo file, in MB. */
export const tettoPerFile = (file) => {
  const tipo = mimePulito(file?.type);
  return tipo.startsWith('audio/') || tipo.startsWith('video/')
    ? TETTO_AUDIO_VIDEO_MB
    : TETTO_DOCUMENTO_MB;
};

/** null se il file ci sta, altrimenti { dimensioneMb, tettoMb }. */
export const sforaIlTetto = (file) => {
  if (!file) return null;
  const dimensioneMb = file.size / (1024 * 1024);
  const tettoMb = tettoPerFile(file);
  return dimensioneMb > tettoMb
    ? { dimensioneMb: Math.round(dimensioneMb), tettoMb }
    : null;
};
